import { SUPPORTED_LOCALES, DEFAULT_LOCALE, type Locale } from './config';

/**
 * Translated URL segments per locale.
 * To add a new language: add one entry here + a JSON file + the locale to SUPPORTED_LOCALES.
 */
const ROUTE_SEGMENTS: Record<string, Record<string, string>> = {
  it: {
    shop: 'negozio',
    about: 'chi-siamo',
    contact: 'contatti',
    cart: 'carrello',
    product: 'prodotto',
    categories: 'categorie',
    legal: 'legale',
    success: 'successo',
  },
  es: {
    shop: 'tienda',
    about: 'nosotros',
    contact: 'contacto',
    cart: 'carrito',
    product: 'producto',
    categories: 'categorias',
    legal: 'legal',
    success: 'exito',
  },
  fr: {
    shop: 'boutique',
    about: 'a-propos',
    contact: 'contact',
    cart: 'panier',
    product: 'produit',
    categories: 'categories',
    legal: 'mentions-legales',
    success: 'succes',
  },
  de: {
    shop: 'shop',
    about: 'ueber-uns',
    contact: 'kontakt',
    cart: 'warenkorb',
    product: 'produkt',
    categories: 'kategorien',
    legal: 'rechtliches',
    success: 'erfolg',
  },
};

/** Category slug translations (URL slugs only) */
const CATEGORY_SLUGS: Record<string, Record<string, string>> = {
  it: {
    rings: 'anelli',
    'earrings-and-hoops': 'orecchini-e-cerchi',
    'unique-finds': 'pezzi-unici',
  },
  es: {
    rings: 'anillos',
    'earrings-and-hoops': 'pendientes-y-aros',
    'unique-finds': 'piezas-unicas',
  },
  fr: {
    rings: 'bagues',
    'earrings-and-hoops': 'boucles-d-oreilles',
    'unique-finds': 'pieces-uniques',
  },
  de: {
    rings: 'ringe',
    'earrings-and-hoops': 'ohrringe-und-creolen',
    'unique-finds': 'einzelstuecke',
  },
};

// Build reverse maps for fast lookup
const REVERSE_SEGMENTS: Record<string, Record<string, string>> = {};
const REVERSE_CATEGORY_SLUGS: Record<string, Record<string, string>> = {};

for (const locale of Object.keys(ROUTE_SEGMENTS)) {
  REVERSE_SEGMENTS[locale] = {};
  for (const [en, translated] of Object.entries(ROUTE_SEGMENTS[locale])) {
    REVERSE_SEGMENTS[locale][translated] = en;
  }
}
for (const locale of Object.keys(CATEGORY_SLUGS)) {
  REVERSE_CATEGORY_SLUGS[locale] = {};
  for (const [en, translated] of Object.entries(CATEGORY_SLUGS[locale])) {
    REVERSE_CATEGORY_SLUGS[locale][translated] = en;
  }
}

/**
 * Get the locale prefix for a given locale.
 * English (default) has no prefix; other locales get `/${locale}`.
 */
function localePrefix(locale: Locale): string {
  return locale === DEFAULT_LOCALE ? '' : `/${locale}`;
}

/**
 * Translate a single URL segment for a locale.
 */
function translateSegment(segment: string, locale: Locale): string {
  if (locale === DEFAULT_LOCALE) return segment;
  return ROUTE_SEGMENTS[locale]?.[segment] ?? segment;
}

/**
 * Translate a category slug for a locale.
 */
export function translateCategorySlug(slug: string, locale: Locale): string {
  if (locale === DEFAULT_LOCALE) return slug;
  return CATEGORY_SLUGS[locale]?.[slug] ?? slug;
}

/**
 * Reverse-translate a category slug back to English.
 */
export function untranslateCategorySlug(slug: string, locale: Locale): string {
  if (locale === DEFAULT_LOCALE) return slug;
  return REVERSE_CATEGORY_SLUGS[locale]?.[slug] ?? slug;
}

/**
 * Generate a locale-aware path.
 *
 * Usage:
 *   localePath('en', '/shop')           → '/shop'
 *   localePath('it', '/shop')           → '/it/negozio'
 *   localePath('it', '/product/:slug', { slug: 'lava-ring' }) → '/it/prodotto/lava-ring'
 *   localePath('it', '/categories/:category', { category: 'rings' }) → '/it/categorie/anelli'
 */
export function localePath(
  locale: Locale,
  path: string,
  params?: Record<string, string>
): string {
  // Handle root path
  if (path === '/') return localePrefix(locale) || '/';

  // Split path and translate each segment
  const segments = path.split('/').filter(Boolean);
  const translated = segments.map((seg) => {
    if (seg.startsWith(':')) {
      const paramName = seg.slice(1);
      const value = params?.[paramName] ?? seg;
      // Translate category slugs
      if (paramName === 'category') {
        return translateCategorySlug(value, locale);
      }
      return value;
    }
    return translateSegment(seg, locale);
  });

  return `${localePrefix(locale)}/${translated.join('/')}`;
}

/**
 * Parse a URL pathname to extract locale and the canonical (English) path.
 *
 * parseLocalePath('/it/negozio')  → { locale: 'it', path: '/shop' }
 * parseLocalePath('/shop')        → { locale: 'en', path: '/shop' }
 */
export function parseLocalePath(pathname: string): {
  locale: Locale;
  path: string;
} {
  const segments = pathname.split('/').filter(Boolean);

  // Check if first segment is a locale prefix
  let locale: Locale = DEFAULT_LOCALE;
  let rest = segments;

  if (
    segments.length > 0 &&
    SUPPORTED_LOCALES.includes(segments[0] as Locale) &&
    segments[0] !== DEFAULT_LOCALE
  ) {
    locale = segments[0] as Locale;
    rest = segments.slice(1);
  }

  if (rest.length === 0) return { locale, path: '/' };

  // Reverse-translate segments back to English
  const reverseMap = REVERSE_SEGMENTS[locale] || {};
  const reverseCatMap = REVERSE_CATEGORY_SLUGS[locale] || {};

  const englishSegments = rest.map((seg, i) => {
    // Check if previous segment was 'categories' (translated)
    const prevEn =
      i > 0 ? reverseMap[rest[i - 1]] || rest[i - 1] : undefined;
    if (prevEn === 'categories' || rest[i - 1] === 'categories') {
      return reverseCatMap[seg] || seg;
    }
    return reverseMap[seg] || seg;
  });

  return { locale, path: `/${englishSegments.join('/')}` };
}

/**
 * Switch the current path to a different locale.
 *
 * switchLocalePath('/it/negozio', 'en')  → '/shop'
 * switchLocalePath('/shop', 'it')        → '/it/negozio'
 * switchLocalePath('/it/prodotto/lava-ring', 'en') → '/product/lava-ring'
 */
export function switchLocalePath(
  currentPath: string,
  targetLocale: Locale
): string {
  const { path } = parseLocalePath(currentPath);
  // Re-resolve dynamic params from the English path
  const segments = path.split('/').filter(Boolean);

  // Detect params: for /product/:slug patterns, the slug is the actual value
  // For /categories/:category, the category is the actual value
  const params: Record<string, string> = {};
  for (let i = 0; i < segments.length; i++) {
    if (segments[i] === 'product' && segments[i + 1]) {
      params.slug = segments[i + 1];
    }
    if (segments[i] === 'categories' && segments[i + 1]) {
      params.category = segments[i + 1];
    }
  }

  return localePath(targetLocale, path, params);
}

/**
 * All route definitions used to generate React Router routes.
 * Each entry maps a route key to its English path pattern.
 */
export const ROUTE_DEFINITIONS = [
  { key: 'home', path: '/' },
  { key: 'shop', path: '/shop' },
  { key: 'about', path: '/about' },
  { key: 'contact', path: '/contact' },
  { key: 'cart', path: '/cart' },
  { key: 'product', path: '/product/:slug' },
  { key: 'categories', path: '/categories/:category' },
  { key: 'legal', path: '/legal' },
  { key: 'success', path: '/success' },
] as const;

/**
 * Generate the translated path pattern for a route in a given locale.
 * Used by React Router for route matching.
 *
 * getRoutePattern('it', '/product/:slug') → '/it/prodotto/:slug'
 */
export function getRoutePattern(locale: Locale, englishPath: string): string {
  if (englishPath === '/') return localePrefix(locale) || '/';

  const segments = englishPath.split('/').filter(Boolean);
  const translated = segments.map((seg) => {
    if (seg.startsWith(':')) return seg; // Keep params as-is for Router
    return translateSegment(seg, locale);
  });

  return `${localePrefix(locale)}/${translated.join('/')}`;
}

export { ROUTE_SEGMENTS, CATEGORY_SLUGS };
